// @ts-nocheck
import {
  Component,
  Host,
  h,
  Prop,
  Watch,
  Element,
  Event,
  EventEmitter,
  Listen,
  Method,
} from '@stencil/core';
import { toggleDisableChildNodes } from '../../utils/toggleDisableChildNodes';
import { closestAll } from './helpers';
import restrictScroll from 'restrict-scroll';
import { Direction, Trigger } from './types';

// Popper.js is only needed for deprecated react-based <aha-menu> components which, due to the fact that
// they are in use in customer extensions, cannot realistically be updated.
import * as Popper from '@popperjs/core';
import './aha-menu-window-events';

export interface AhaMenu extends HTMLAhaMenuElement {}

@Component({
  tag: 'aha-menu',
  styleUrl: 'aha-menu.css',
  shadow: true,
})
export class AhaMenu {
  @Element() host!: HTMLAhaMenuElement;

  private controlElem!: HTMLElement;

  /**
   * Triggers when the menu is closed using the escape key.
   */
  @Event({
    eventName: 'aha-menu:cancel',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  cancelEvent!: EventEmitter;

  /**
   * Triggers when the menu is initially opened but before it is visible.
   */
  @Event({
    eventName: 'aha-menu:open',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  openEvent!: EventEmitter;

  // If an <aha-menu> is opened and it has a child <aha-menu-content>, ensure the <aha-menu-content> is also opened.
  @Listen('aha-menu:open')
  private openMenuContent() {
    if (this.useDeprecatedRender) {
      return;
    }

    this.positionPopper();
    const menuContent = this.getMenuContent();
    if (menuContent) {
      menuContent.open = true;
    }
  }

  /**
   * Triggers when the menu is visible on the page.
   */
  @Event({
    eventName: 'aha-menu:opened',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  openedEvent!: EventEmitter;

  /**
   * Triggers when the menu is initially closed but before it is hidden.
   */
  @Event({
    eventName: 'aha-menu:close',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  closeEvent!: EventEmitter;

  // If an <aha-menu> is closed and it has a portaled child <aha-menu-content>, ensure the <aha-menu-content> is also closed.
  @Listen('aha-menu:close')
  private closeMenuContent(event: CustomEvent) {
    if (event.detail !== this.host) {
      // this event propagates up the DOM tree, and we don't want to close the parent menu when its submenu closes
      return;
    }

    if (this.useDeprecatedRender) {
      return;
    }

    const menuContent = this.getMenuContent();
    if (menuContent) {
      menuContent.open = false;
    }
  }

  /**
   * Triggers when the menu is no longer visible on the page.
   */
  @Event({
    eventName: 'aha-menu:closed',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  closedEvent!: EventEmitter;

  /** Whether or not the menu is a submenu of another menu. If it is, then a caret icon is automatically added to the submenu's control. */
  @Prop({ reflect: true, mutable: true }) submenu: boolean = false;

  /** Whether the menu should open on hover or click. */
  @Prop({ reflect: true }) trigger: Trigger = 'click';

  /** Whether or not the menu is currently open. */
  @Prop({ reflect: true, mutable: true }) open: boolean = false;
  @Watch('open')
  toggleOpen() {
    if (this.disabled) return;

    if (this.open) {
      // Opening the menu.
      this.openEvent.emit(this.host);

      // NOTE: Deprecated rendering path for pre-existing (and non-upgradable) customer extensions.
      if (this.useDeprecatedRender) {
        this.popperInstance = Popper.createPopper(this.controlElem, this.popup, {
          placement: 'bottom-start',
          strategy: 'fixed',
          modifiers: [
            {
              name: 'flip',
              enabled: true,
              options: {
                fallbackPlacements: ['bottom-start', 'bottom-end', 'top-start', 'top-end'],
              },
            },
          ],
        });

        // Position popper where it should be.
        this.popperInstance.forceUpdate();

        restrictScroll.add(this.popup);
      }
    } else {
      // Closing the menu.
      this.closeEvent.emit(this.host);

      // NOTE: Deprecated rendering path for pre-existing (and non-upgradable) customer extensions.
      if (this.useDeprecatedRender) {
        delete this.popperInstance;
        restrictScroll.delete(this.popup);
      }
    }
  }

  toggle() {
    if (this.disabled) return;
    this.open = !this.open;
  }

  show() {
    if (this.disabled) return;
    this.open = true;
  }

  hide() {
    if (this.disabled) return;
    this.open = false;
  }

  // Because the aha-menu component only knows if it is opened if its child aha-menu-content element is opened, we expose this method
  // to allow the aha-menu-content element to tell the aha-menu element that it should fire its own opened event.
  @Method()
  async fireOpenedEvent() {
    if (this.open) {
      this.openedEvent.emit(this.host);
    }
  }

  // Because the aha-menu component only knows if it is closed if its child aha-menu-content element is closed, we expose this method
  // to allow the aha-menu-content element to tell the aha-menu element that it should fire its own closed event.
  @Method()
  async fireClosedEvent() {
    if (this.open) this.open = false;
    this.closedEvent.emit(this.host);
  }

  // Because the aha-menu component only knows if it is cancelled if its child aha-menu-content element is cancelled, we expose this method
  // to allow the aha-menu-content element to tell the aha-menu element that it should fire its own cancelled event.
  @Method()
  async fireCancelEvent() {
    if (this.open) this.open = false;
    this.cancelEvent.emit(this.host);
  }

  /** Whether or not the menu is currently disabled. */
  @Prop({ reflect: true }) disabled: boolean = false;
  private disableChildren() {
    toggleDisableChildNodes(this.host);
  }

  /** When true, prevent a menu from being closed. */
  // FIXME: Not currently working due to refactor.
  @Prop({ reflect: true, attribute: 'disable-close' }) disableClose: boolean = false;

  private popper!: HTMLAhaMenuElement;

  get isNestedMenu() {
    return this.host.closest('aha-menu-item')
      ? closestAll(this.host, 'aha-menu-content').length
      : false;
  }

  private getMenuContent() {
    return this.host.querySelector(':scope > aha-menu-content');
  }

  /** An option that, when set, overrides the automatic positioning of the embedded `<aha-menu-content>` and forces it to display in the specified direction. */
  @Prop({ reflect: true }) direction?: Direction;
  @Watch('direction')
  private repositionPopper() {
    if (/[A-Z]/.test(this.direction)) this.direction = this.direction.toLowerCase();
    this.positionPopper();
  }

  // Logic to determine the direction a menu "pops" open.
  // Note that the logic is subtely different for sub-menus, as we typically want them to pop to the side.
  private positionPopper() {
    // deprecated render uses Popper for positioning
    if (this.useDeprecatedRender) {
      return;
    }

    if (!this.controlElem) {
      // resize event may call positionPopper while controlElem is still undefined.
      return;
    }
    const box = this.controlElem.getBoundingClientRect();
    const { top, bottom, left, right, width, height } = box;
    const centerX = left + width / 2;
    const centerY = top + height / 2;

    const docBox = {
      height: document.documentElement.clientHeight,
      width: document.documentElement.clientWidth,
    };

    // If a direction is specified, then we need to set the _opposite_ direction's value to get the proper display CSS.
    const directions = this.direction?.split(' ') || [];
    let verticalGrowsFrom = directions.includes('top')
      ? 'bottom'
      : directions.includes('bottom')
      ? 'top'
      : '';
    let horizontalGrowsFrom = directions.includes('left')
      ? 'right'
      : directions.includes('right')
      ? 'left'
      : '';

    let verticalValue;
    let horizontalValue;
    let maxHeight;
    let maxWidth;

    const menuContent = this.getMenuContent()!;

    if (this.isNestedMenu) {
      verticalGrowsFrom = centerY < docBox.height / 2 ? 'top' : 'bottom';
      verticalValue = verticalGrowsFrom === 'top' ? top : docBox.height - bottom;
      verticalValue = `${verticalValue}px`;
      maxHeight = verticalGrowsFrom === 'top' ? docBox.height - top : bottom;

      horizontalGrowsFrom = centerX < docBox.width / 2 ? 'left' : 'right';
      horizontalValue = horizontalGrowsFrom === 'left' ? right : docBox.width - left;
      horizontalValue = `calc(${horizontalValue}px + var(--spacer-2))`;
      maxWidth = horizontalGrowsFrom === 'left' ? docBox.width - left : right;
    } else {
      verticalGrowsFrom ||= centerY < docBox.height / 2 ? 'top' : 'bottom';
      verticalValue = verticalGrowsFrom === 'top' ? bottom : docBox.height - top;
      verticalValue = `${verticalValue}px`;
      maxHeight = verticalGrowsFrom === 'top' ? docBox.height - bottom : top;

      horizontalGrowsFrom ||= centerX < docBox.width / 2 ? 'left' : 'right';
      horizontalValue = horizontalGrowsFrom === 'left' ? left : docBox.width - right;
      horizontalValue = `${horizontalValue}px`;
      maxWidth = horizontalGrowsFrom === 'left' ? docBox.width - left : right;
    }

    ['top', 'bottom'].forEach(direction => {
      menuContent.style.setProperty(
        `--aha-menu-content--${direction}`,
        verticalGrowsFrom === direction ? verticalValue : 'auto',
      );
    });

    ['left', 'right'].forEach(direction => {
      menuContent.style.setProperty(
        `--aha-menu-content--${direction}`,
        horizontalGrowsFrom === direction ? horizontalValue : 'auto',
      );
    });

    menuContent.style.setProperty('--aha-menu-content--max-height', `${maxHeight}px`);
    menuContent.style.setProperty('--aha-menu-content--max-width', `${maxWidth}px`);
  }

  connectedCallback() {
    this.handleButtonSlot();
    this.submenu ||= this.isNestedMenu;
  }

  // If an `<aha-menu>` has the `open` attribute set upon creation, then open that menu.
  componentDidLoad() {
    if (this.open)
      // Need to wait a tick for the web component to fully populate all its slots, or the menu will appear in the wrong position.
      requestAnimationFrame(() => {
        this.toggleOpen();
      });
  }

  // Clean up any entangledElements.
  disconnectedCallback() {
    // For deprecated usages of <aha-menu>.
    this.deprecatedDisconnectedCallback();
  }

  // If the user presses the "Escape" key, then close the menu.
  private handleKeyup(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      const host = event.currentTarget;
      host.open = false;
      host.cancelEvent.emit(host);
    }
  }

  // _DEPRECATED_: Maintain for backwards compatibility with aha-develop.
  // Moves anything in the "button" slot into the "control" slot.
  private handleButtonSlot() {
    this.host.querySelectorAll('[slot="button"]').forEach(elem => {
      elem.slot = 'control';
    });
  }

  // --------------------------------------------------------------------------------
  // --------------------------------------------------------------------------------
  // --- START -------------------- Stuff needed for deprecated uses of <aha-menu>.
  // --------------------------------------------------------------------------------
  // --------------------------------------------------------------------------------
  private get containsFocus() {
    return this.host.matches(':focus-within');
  }

  @State() useDeprecatedRender: boolean = false;
  @Watch('useDeprecatedRender')
  private handleDeprecatedRender() {
    if (this.useDeprecatedRender) {
      window.addEventListener('focusout', this.closeOnBlur);
    } else {
      window.removeEventListener('focusout', this.closeOnBlur);
    }
  }

  // The thing which pops open on the page, whether it is an <aha-menu-content>, or, in the case of the `useDeprecatedRender` path, just some plain old HTML.
  private popperInstance: Popper.Instance | undefined;

  // NOTE: Only used in the `useDeprecatedRender` path.
  private popup!: HTMLElement;

  // If the <aha-menu> does NOT have a <aha-menu-content> within it, then this usage of <aha-menu> is likely an
  // instance of deprecated code coming from an existing customer extension, and as such cannot safely be upgraded.
  // In this case, we should NOT lift the menu's contents to the <aha-portal-outlet> or to the <body>, as doing
  // either would break react event handling for the elements inside <aha-menu>. Instead, we just use popper.js to
  // position things, and accept that this content can still have issues with overflow, z-index, etc. If we don't
  // like it, the only possible way around it is to upgrade existing customer extensions.
  private checkForAhaMenuContent(event: any) {
    const host = event.target.closest('aha-menu') || event.target.getRootNode().host;
    const childNodes = event.target.assignedNodes();

    // If the <aha-menu> does not have a <aha-menu-content> within it, then use the deprecated rendering path.
    if (host) {
      // if (!childNodes.find(node => node.tagName === 'AHA-MENU-CONTENT')) {
      const defaultElements = host.shadowRoot
        .querySelector('slot:not([name]), slot[name=""]')
        .assignedElements();
      if (
        defaultElements.length > 0 &&
        !childNodes.find(node => node.tagName === 'AHA-MENU-CONTENT')
      ) {
        host.useDeprecatedRender = true;
      } else {
        host.useDeprecatedRender = false;
      }
    }
  }

  private get menuItems() {
    return Array.from(
      this.host.querySelectorAll<HTMLAhaMenuItemElement>('aha-menu-item:not([disabled])') || [],
    );
  }
  private handleMenuClick = (event: MouseEvent) => {
    if (!this.useDeprecatedRender) return;

    const composedPath = event.composedPath();
    const menuActionsSlot = composedPath.find(
      el => el instanceof HTMLSlotElement && el.name === 'menu-actions',
    );
    const menuItem = composedPath.find(el => (el as Element).tagName === 'AHA-MENU-ITEM');
    if (menuActionsSlot || !menuItem) {
      this.popup.focus();
      return;
    }

    this.hide();
  };

  /** _DEPRECATED:_ Triggers when the open state of the menu changes. Has been replaced with the `aha-menu:opened` event. */
  @Event() openChanged!: EventEmitter<boolean>;

  /** _DEPRECATED:_ Triggers when the menu is closed using the escape key. Has been replaced with the `aha-menu:cancel` event. */
  @Event() cancel!: EventEmitter<any>;

  /** _DEPRECATED:_ The label for the menu, which will be inserted into the opener button. The entire button can be overridden using the `control` slot. */
  @Prop() label?: string | undefined;
  @Watch('label')
  deprecatedGenerateControl() {
    if (this.label) {
      let control = this.host.querySelector('[slot="control"]');
      if (!control) {
        control = document.createElement('aha-button');
        control.slot = 'control';
        this.host.insertAdjacentElement('afterbegin', control);
      }
      control.innerText = this.label;
      this.host.removeAttribute('label');
    }
  }

  private deprecatedDisconnectedCallback() {
    if (!this.useDeprecatedRender) return;

    window.removeEventListener('focusout', this.closeOnBlur);
    this.popperInstance?.destroy();
  }

  private closeOnBlur = () => {
    requestAnimationFrame(() => {
      if (!this.containsFocus) {
        this.hide();
      }
    });
  };
  // --------------------------------------------------------------------------------
  // --------------------------------------------------------------------------------
  // --- END ----------------- Stuff needed for deprecated uses of <aha-menu>.
  // --------------------------------------------------------------------------------
  // --------------------------------------------------------------------------------

  private submenuHideTimeout: number = undefined;

  private submenuHideFn = () => {
    const host = this;
    host.submenuHideTimeout = setTimeout(() => {
      host.hide();
    }, 250);
  };

  private handleMouseLeave(event: MouseEvent) {
    const host = event.target.getRootNode().host;
    if (!host.submenu && host.trigger !== 'hover') return;

    // We consider portaledStuff to be the aha-menu's closest aha-menu-content (since submenus don't butt up against the
    // triggering element, but rather butts up against the triggering element's parent aha-menu-content) as well as any child
    // element
    const inPortaledMenus = [host.closest('aha-menu-content')]
      .concat(Array.from(host.querySelectorAll('*')))
      .filter(node => node === event.relatedTarget);

    if (!inPortaledMenus.length) host.submenuHideFn();
  }

  private handleMouseEnter(event: MouseEvent) {
    const host = event.target.getRootNode().host;
    if (host.submenu || host.trigger === 'hover') {
      clearTimeout(host.submenuHideTimeout);

      // Find any other open submenus which are not an ancestor/descendant of this submenu, and close them.
      const relatedMenuContents = closestAll(host, 'aha-menu-content').concat(
        Array.from(host.querySelectorAll('aha-menu-content')),
      );
      const portaledMenuContents = document.querySelectorAll('aha-menu-content');
      const unrelatedMenuContents = new Set(portaledMenuContents);
      relatedMenuContents.forEach(node => {
        if (unrelatedMenuContents.has(node)) unrelatedMenuContents.delete(node);
      });
      Array.from(unrelatedMenuContents).forEach(menuContent => {
        menuContent.hide();
      });

      // Show this menu.
      host.show();
    }
  }

  private handleClick(event: MouseEvent) {
    const host = event.currentTarget.getRootNode().host;
    if (host.trigger !== 'click') return;

    host.toggle();
  }

  render() {
    const defaultSlot = <slot onSlotchange={this.checkForAhaMenuContent}></slot>;

    return (
      <Host onKeyup={this.handleKeyup} aria-expanded={this.open}>
        <div
          id="control"
          part="control"
          ref={ref => (this.controlElem = ref)}
          onClick={this.handleClick}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
        >
          <slot name="control" onSlotchange={this.handleControlSlotChange}>
            <aha-button part="default-control">
              {this.label || (
                <fa-icon aria-label="More actions..." class="fa-solid fa-ellipsis"></fa-icon>
              )}
            </aha-button>
          </slot>
          {/* _DEPRECATED: only including the "button" slot for backwards compatibility with aha-develop usage. */}
          <slot name="button" onSlotchange={this.handleButtonSlot}></slot>
        </div>

        {this.useDeprecatedRender ? (
          <div
            id="popup"
            role="menu"
            ref={ref => (this.popup = ref)}
            style={{ visibility: this.open ? 'visible' : 'hidden' }}
            onClick={this.handleMenuClick}
            tabIndex={-1}
          >
            {defaultSlot}
          </div>
        ) : (
          defaultSlot
        )}
      </Host>
    );
  }
}
